import axios from 'axios';
import { TradingIdeaExists, TradingIdea, TradingIdeasAccess, TradingIdeasFeedResponse, TradingIdeasResponse } from '@/models/trading-ideas';

const BASE = '/interface/api/market_intelligence';

// Feature-access probe — always 200, { enabled: boolean }. Callers
// should treat any failure as "not enabled" (fail closed); the list
// endpoint 403s for users without access anyway.
export const getTradingIdeasAccess = async (signal?: AbortSignal): Promise<TradingIdeasAccess> => {
    const res = await axios.get<TradingIdeasAccess>(`${BASE}/trading_ideas/access/`, { signal });
    return { enabled: !!res.data?.enabled };
};

export interface TradingIdeasFilters {
    // YYYY-MM-DD — single-batch mode. Use getTradingIdeasFeed for the
    // default (date-less) flat feed.
    date?: string;
}

// Single-batch mode (?date=YYYY-MM-DD) — the old envelope shape, no
// cursor.
export const getTradingIdeas = async (filters: TradingIdeasFilters = {}, signal?: AbortSignal): Promise<TradingIdeasResponse> => {
    const params: Record<string, any> = {};
    if (filters.date) params.date = filters.date;

    const res = await axios.get(`${BASE}/trading_ideas/`, { params, signal });
    const data = res.data;

    // The backend is being built in parallel — normalise here so the
    // component only ever sees TradingIdeasResponse, whether the wire
    // ships a bare array or the MI-style { length, rows } envelope.
    const rows: TradingIdea[] = Array.isArray(data) ? data : Array.isArray(data?.rows) ? data.rows : [];
    const sorted = [...rows].sort((a, b) => (a.rankedPosition ?? 0) - (b.rankedPosition ?? 0));

    return {
        batchDate: (!Array.isArray(data) && data?.batchDate) || sorted[0]?.batchDate || null,
        // Backend-owned disclosure text — single source of truth when
        // present; the page falls back to its built-in copy otherwise.
        disclaimer: (!Array.isArray(data) && typeof data?.disclaimer === 'string' && data.disclaimer) || null,
        length: sorted.length,
        rows: sorted,
    };
};

export interface TradingIdeasFeedFilters {
    // Opaque continuation token from a previous page's nextCursor.
    cursor?: string;
    // Case-insensitive exact ticker filter; composable with cursor.
    ticker?: string;
}

// Default flat-feed mode — newest rows across batches, ordered
// (-batchDate, rankedPosition), cursor-paginated. The server owns the
// ordering; no client-side sort here.
export const getTradingIdeasFeed = async (filters: TradingIdeasFeedFilters = {}, signal?: AbortSignal): Promise<TradingIdeasFeedResponse> => {
    const params: Record<string, any> = {};
    if (filters.cursor) params.cursor = filters.cursor;
    if (filters.ticker) params.ticker = filters.ticker;

    const res = await axios.get(`${BASE}/trading_ideas/`, { params, signal });
    const data = res.data;

    const rows: TradingIdea[] = Array.isArray(data) ? data : Array.isArray(data?.rows) ? data.rows : [];

    return {
        batchDate: (!Array.isArray(data) && data?.batchDate) || rows[0]?.batchDate || null,
        disclaimer: (!Array.isArray(data) && typeof data?.disclaimer === 'string' && data.disclaimer) || null,
        nextCursor: (!Array.isArray(data) && typeof data?.nextCursor === 'string' && data.nextCursor) || null,
        rows,
    };
};

// Detail — one full row. 403 for users without access (fail-soft in
// the page), 404 when the id doesn't exist.
export const getTradingIdea = async (id: number, signal?: AbortSignal): Promise<TradingIdea> => {
    const res = await axios.get<TradingIdea>(`${BASE}/trading_ideas/${id}/`, { signal });
    return res.data;
};

// Probe for the stock page. Never throws for "no access" — the backend
// answers 200 with exists=false, so the caller cannot tell the two
// apart and neither can the reader.
export const getTradingIdeaExists = async (ticker: string, signal?: AbortSignal, exchange?: string): Promise<TradingIdeaExists> => {
    const params = exchange ? { exchange } : undefined;
    const res = await axios.get<TradingIdeaExists>(`${BASE}/trading_ideas/${ticker.toUpperCase()}/exists/`, { signal, params });
    return res.data;
};
